<template>
  <div class="regions-page">
    <!-- 헤더 -->
    <div class="page-header">
      <h1 class="page-title">🗺️ 지역별 현장</h1>
      <p class="page-subtitle">원하는 지역의 분양 현장을 찾아보세요</p>
    </div>

    <!-- 지역 탭 -->
    <div class="region-tabs-wrapper">
      <div class="region-tabs">
        <button
          v-for="region in regions"
          :key="region"
          class="region-tab"
          :class="{ active: selectedRegion === region }"
          @click="selectRegion(region)"
        >
          {{ region }}
        </button>
      </div>
    </div>

    <!-- 공고 리스트 -->
    <div class="jobs-container">
      <!-- 로딩 -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>공고를 불러오는 중...</p>
      </div>

      <!-- 공고 없음 -->
      <div v-else-if="jobs.length === 0" class="empty-state">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#adb5bd" stroke-width="1.5">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 15h8M9 9h.01M15 9h.01"/>
        </svg>
        <p>{{ selectedRegion }} 지역에 등록된 공고가 없습니다</p>
        <router-link to="/jobs/create" class="create-btn">공고 등록하기</router-link>
      </div>

      <!-- 공고 그리드 -->
      <div v-else class="jobs-grid">
        <div 
          v-for="job in jobs" 
          :key="job.id" 
          class="job-card"
          :class="{ 'region-ad': isRegionAd(job) }"
          @click="$router.push(`/jobs/${job.id}`)"
        >
          <!-- 썸네일 -->
          <div class="card-thumbnail">
            <img 
              v-if="job.thumbnail" 
              :src="job.thumbnail" 
              :alt="job.title"
            />
            <div v-else class="no-thumbnail">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#adb5bd" stroke-width="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
            
            <!-- 뱃지들 -->
            <div class="card-badges">
              <span v-if="isRegionAd(job)" class="badge region-badge">📍 {{ job.region }}</span>
              <span v-if="job.isPremium && !isExpired(job.isPremiumUntil)" class="badge premium">💎 프리미엄</span>
              <span v-if="job.isUrgent && !isExpired(job.isUrgentUntil)" class="badge urgent">🔥 급구</span>
              <span v-if="job.isPick && !isExpired(job.isPickUntil)" class="badge pick">⭐ PICK</span>
            </div>

            <!-- D-Day -->
            <div v-if="job.deadline" class="card-dday">
              {{ getDday(job.deadline) }}
            </div>
          </div>

          <!-- 카드 바디 -->
          <div class="card-body">
            <h3 class="card-title">{{ job.title }}</h3>
            <p class="card-site">{{ job.siteName }}</p>
            
            <div class="card-info">
              <span class="info-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                {{ job.region }}
              </span>
              <span class="info-item">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                </svg>
                {{ job.jobType }}
              </span>
            </div>

            <div class="card-salary">
              <span class="salary-type">{{ job.salaryType }}</span>
              <span class="salary-amount">{{ job.salary }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 더보기 버튼 -->
      <div v-if="hasMore && !loading" class="load-more">
        <button @click="loadMore" class="load-more-btn" :disabled="loadingMore">
          {{ loadingMore ? '불러오는 중...' : '더보기' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { collection, query, where, orderBy, limit, startAfter, getDocs } from 'firebase/firestore'
import { db } from '@/services/firebase'

export default {
  name: 'RegionsView',
  setup() {
    const regions = [
      '서울', '경기', '인천', '대전', '세종', '충북', '충남',
      '전북', '전남', '광주', '경북', '경남', '대구', '울산', '부산', '강원', '제주'
    ]

    const selectedRegion = ref('서울')
    const jobs = ref([])
    const loading = ref(false)
    const loadingMore = ref(false)
    const hasMore = ref(false)
    const lastDoc = ref(null)
    const pageSize = 12

    // 지역 선택
    const selectRegion = (region) => {
      selectedRegion.value = region
      fetchJobs()
    }

    // 공고 조회
    const fetchJobs = async () => {
      loading.value = true
      jobs.value = []
      lastDoc.value = null

      try {
        // 지역 광고 공고 먼저
        const adQuery = query(
          collection(db, 'jobs'),
          where('region', '==', selectedRegion.value),
          where('status', '==', 'active'),
          where('isRegionAd', '==', true),
          orderBy('createdAt', 'desc')
        )
        
        const adSnapshot = await getDocs(adQuery)
        const adJobs = adSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(job => !isExpired(job.regionAdUntil))

        // 일반 공고
        const normalQuery = query(
          collection(db, 'jobs'),
          where('region', '==', selectedRegion.value),
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc'),
          limit(pageSize)
        )

        const normalSnapshot = await getDocs(normalQuery)
        const normalJobs = normalSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))

        // 지역 광고 아닌 것만 필터 (중복 제거)
        const adIds = new Set(adJobs.map(j => j.id))
        const filteredNormalJobs = normalJobs.filter(j => !adIds.has(j.id))

        jobs.value = [...adJobs, ...filteredNormalJobs]
        lastDoc.value = normalSnapshot.docs[normalSnapshot.docs.length - 1]
        hasMore.value = normalSnapshot.docs.length >= pageSize

      } catch (e) {
        console.error('공고 조회 실패:', e)
      } finally {
        loading.value = false
      }
    }

    // 더보기
    const loadMore = async () => {
      if (!lastDoc.value || loadingMore.value) return

      loadingMore.value = true

      try {
        const q = query(
          collection(db, 'jobs'),
          where('region', '==', selectedRegion.value),
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc.value),
          limit(pageSize)
        )

        const snapshot = await getDocs(q)
        const newJobs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        
        // 지역 광고 제외 (이미 상단에 있음)
        const existingIds = new Set(jobs.value.map(j => j.id))
        const filteredJobs = newJobs.filter(j => !existingIds.has(j.id))

        jobs.value.push(...filteredJobs)
        lastDoc.value = snapshot.docs[snapshot.docs.length - 1]
        hasMore.value = snapshot.docs.length >= pageSize

      } catch (e) {
        console.error('더보기 실패:', e)
      } finally {
        loadingMore.value = false
      }
    }

    // 지역 광고 여부
    const isRegionAd = (job) => {
        return job.isRegionAd && !isExpired(job.isRegionAdUntil)
    }

    // 만료 체크
    const isExpired = (timestamp) => {
      if (!timestamp) return true
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date < new Date()
    }

    // D-Day 계산
    const getDday = (deadline) => {
      if (!deadline) return ''
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const target = new Date(deadline)
      target.setHours(0, 0, 0, 0)
      const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24))
      
      if (diff < 0) return '마감'
      if (diff === 0) return 'D-Day'
      return `D-${diff}`
    }

    // 지역 변경 감지
    watch(selectedRegion, () => {
      fetchJobs()
    })

    onMounted(() => {
      fetchJobs()
    })

    return {
      regions,
      selectedRegion,
      jobs,
      loading,
      loadingMore,
      hasMore,
      selectRegion,
      loadMore,
      isRegionAd,
      isExpired,
      getDday
    }
  }
}
</script>

<style scoped>
.regions-page {
  min-height: 100vh;
  background: #f9fafb;
}

/* 헤더 */
.page-header {
  background: #fff;
  padding: 40px 24px 24px;
  border-bottom: 1px solid #f2f4f6;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 15px;
  color: #6b7684;
}

/* 지역 탭 */
.region-tabs-wrapper {
  background: #fff;
  border-bottom: 1px solid #f2f4f6;
  position: sticky;
  top: 60px;
  z-index: 10;
}

.region-tabs {
  display: flex;
  gap: 4px;
  padding: 16px 24px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.region-tabs::-webkit-scrollbar {
  display: none;
}

.region-tab {
  padding: 10px 18px;
  background: #f2f4f6;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7684;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}

.region-tab:hover {
  background: #e5e8eb;
}

.region-tab.active {
  background: #191f28;
  color: #fff;
}

/* 공고 컨테이너 */
.jobs-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

/* 로딩 & 빈 상태 */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #6b7684;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e8eb;
  border-top-color: #3182f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state svg {
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 15px;
  margin-bottom: 20px;
}

.create-btn {
  padding: 12px 24px;
  background: #3182f6;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.15s;
}

.create-btn:hover {
  background: #1b64da;
}

/* 공고 그리드 */
.jobs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

/* 공고 카드 */
.job-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: all 0.2s;
}

.job-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.job-card.region-ad {
  border: 2px solid #3182f6;
}

/* 썸네일 */
.card-thumbnail {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #f2f4f6;
}

.card-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-thumbnail {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 뱃지 */
.card-badges {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.badge {
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.badge.region-badge {
  background: #3182f6;
  color: #fff;
}

.badge.premium {
  background: #fef3c7;
  color: #b45309;
}

.badge.urgent {
  background: #fee2e2;
  color: #dc2626;
}

.badge.pick {
  background: #dcfce7;
  color: #15803d;
}

/* D-Day */
.card-dday {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 5px 10px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
}

/* 카드 바디 */
.card-body {
  padding: 16px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-site {
  font-size: 14px;
  color: #6b7684;
  margin-bottom: 12px;
}

.card-info {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #8b95a1;
}

.card-salary {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.salary-type {
  font-size: 12px;
  color: #8b95a1;
}

.salary-amount {
  font-size: 16px;
  font-weight: 700;
  color: #3182f6;
}

/* 더보기 */
.load-more {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.load-more-btn {
  padding: 14px 40px;
  background: #fff;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #4e5968;
  cursor: pointer;
  transition: all 0.15s;
}

.load-more-btn:hover:not(:disabled) {
  border-color: #191f28;
  color: #191f28;
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 반응형 */
@media (max-width: 768px) {
  .page-header {
    padding: 24px 16px 20px;
  }

  .page-title {
    font-size: 24px;
  }

  .region-tabs {
    padding: 12px 16px;
  }

  .region-tab {
    padding: 8px 14px;
    font-size: 13px;
  }

  .jobs-container {
    padding: 16px;
  }

  .jobs-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
</style>